import {
  IconArrowLeft,
  IconCrossLarge,
  IconMap,
  IconNav,
  IconQuestionCircle,
  IconSearch,
} from '@tock/icons';
import { IconButton, TockLogoAnchor, Typography } from 'omakase';
import { ShrinkButtonsToIconsContext } from '../Button/ShrinkingHeaderButton';
import { SiteInfo } from 'components/header/SiteInfo';
import { Theme, css } from '@emotion/react';
import {
  mobileHeaderSlimLeft,
  mobileHeaderSlimRight,
  omakaseHeaderCenterItems,
  omakaseHeaderLeftItems,
  omakaseHeaderMobileContainer,
  omakaseHeaderMobileMain,
  omakaseHeaderRightItems,
  omakaseHeaderShadow,
} from './headerCssDefs';
import { useHeaderScrollShadow } from 'omakase/Header/useHeaderScrollShadow';
import React from 'react';

interface HasClickHandler {
  onClick: () => void;
}

export interface Absent {
  type: 'Absent';
}

interface TockLogo {
  type: 'TockLogo';
  config?: {
    href: string;
    'aria-label': string;
  };
}

interface LeftButton extends HasClickHandler {
  type: 'LeftButton';
}

interface TitleTextWithLeftButton extends HasClickHandler {
  type: 'TitleTextWithLeftButton';
  text: string;
}

interface MapButton extends HasClickHandler {
  type: 'MapButton';
}

export interface TitleText {
  type: 'TitleText';
  text: string;
}

type LeftContent = TockLogo | LeftButton | MapButton | TitleText | Absent | TitleTextWithLeftButton;

interface CenterButton {
  type: 'CenterButton';
  button: React.ReactElement;
  // If true, the button content should take over the whole header. In the event
  // that LeftButton, or RightButton, or AuxiliarySearch is defined, it is ignored.
  // The takeover button will generally be a whole-titlebar primary button, or a
  // whole-titlebar search box.
  isTakeover: boolean;
}
// The assumption is that CenterButton will do its own event handling, there's no
// reason to thread event handling through Header's own implementation.

type CenterContent = CenterButton | TitleText | TockLogo | Absent;

interface AuxiliaryButtons {
  type: 'AuxiliaryButtons';
  // This typing is designed to be compatible with a children= prop. Be sure to
  // include key= props on each button.
  buttons: React.ReactNode;
  /**
   * Relevant for desktop rendering only -- will slim down the margins
   * inserted to the left of the auxiliary buttons.
   */
  slimLeft?: boolean;
}

export type AuxiliaryContent = AuxiliaryButtons | Absent;

interface CloseButton extends HasClickHandler {
  type: 'CloseButton';
}

interface HamburgerButton extends HasClickHandler {
  type: 'HamburgerButton';
}

interface SearchIconButton extends HasClickHandler {
  type: 'SearchIconButton';
}

interface SearchWideButton extends HasClickHandler {
  type: 'SearchWideButton';
}

interface HelpButton extends HasClickHandler {
  type: 'HelpButton';
  'aria-label': string;
}

export interface AvatarOrAuthenticationButtons {
  type: 'AvatarOrAuthenticationButtons';
  isAuthenticated: boolean;
  isPartner: boolean;
  /**
   * The pathname for the page the user is currently visiting; used
   * for the login interface's continue= URL query argument.
   */
  pathname: string;
  guestAccountInfo?: {
    name: string;
    profileImageUrl: string;
  };
}

type MobileRightContent =
  CloseButton | HamburgerButton | HelpButton | SearchIconButton | SearchWideButton | Absent;

export type DesktopRightContent = MobileRightContent | AvatarOrAuthenticationButtons;

export type SlimHeaderSpec = 'defaultForContent' | 'no' | 'yes';
export interface HeaderBaseProps {
  hasShadow: boolean;
  leftContent: LeftContent;
  centerContent: CenterContent;
  auxiliaryContent: AuxiliaryContent;
  slimLeft?: SlimHeaderSpec;
  slimRight?: SlimHeaderSpec;
  bumpZIndex?: boolean;
  additionalContent?: React.ReactNode;
}
export interface HeaderProps extends HeaderBaseProps {
  rightContent: MobileRightContent;
  isStorybook?: boolean;
}

export const Text = ({ text }: { text: string }) => {
  return (
    <Typography variant="heading04" component="h1">
      {text}
    </Typography>
  );
};

// TODO(matthew): there's no i18n for the aria labels

export function transformRightContentIntoInfo(
  rightContent: MobileRightContent | DesktopRightContent
):
  | {
      Icon: React.FunctionComponent;
      ariaLabel: string;
      isPrimary: boolean;
      onClick: () => void;
    }
  | undefined {
  // The noop() doesn't actually come into play in any relevant case.
  const onClick = 'onClick' in rightContent ? rightContent.onClick : () => undefined;
  let Icon,
    ariaLabel,
    isPrimary = false;

  switch (rightContent.type) {
    case 'Absent':
    case 'AvatarOrAuthenticationButtons':
      return undefined;
    case 'CloseButton':
      Icon = IconCrossLarge;
      ariaLabel = 'Close';
      break;
    case 'HelpButton':
      Icon = IconQuestionCircle;
      ariaLabel = rightContent['aria-label'];
      break;
    case 'HamburgerButton':
      Icon = IconNav;
      ariaLabel = 'Menu';
      break;
    case 'SearchIconButton':
    case 'SearchWideButton':
      // There's no difference between these in the mobile case
      Icon = IconSearch;
      ariaLabel = 'Search';
      isPrimary = true;
      break;
  }
  return {
    Icon,
    ariaLabel,
    isPrimary,
    onClick,
  };
}

export function defaultSlimHeaderSpecForContent(
  content: LeftContent | MobileRightContent | DesktopRightContent,
  searchWideIsDistinct: boolean
): 'no' | 'yes' {
  let { type } = content;
  if (type === 'SearchWideButton' && !searchWideIsDistinct) {
    type = 'SearchIconButton';
  }
  switch (type) {
    case 'Absent':
    case 'AvatarOrAuthenticationButtons':
    case 'TitleText':
    case 'TockLogo':
    case 'SearchIconButton':
    case 'SearchWideButton':
      // Both SearchIconButton & SearchWideButton are primary buttons, not ghost
      // buttons, so we should use normal spacing.
      return 'no';
    default:
      return 'yes';
  }
}

const Header = ({
  auxiliaryContent,
  additionalContent,
  centerContent,
  hasShadow,
  leftContent,
  rightContent,
  bumpZIndex,
  slimLeft = 'defaultForContent',
  slimRight = 'defaultForContent',
  isStorybook = false,
  ...rest
}: HeaderProps) => {
  const showShadow = useHeaderScrollShadow(hasShadow);

  let leftItem: React.ReactElement | undefined;
  let centerItem: React.ReactElement | undefined;
  let rightItems;

  if ('isTakeover' in centerContent && centerContent.isTakeover) {
    centerItem = centerContent.button;
  } else {
    switch (leftContent.type) {
      case 'TockLogo':
        leftItem = (
          <>
            <TockLogoAnchor
              aria-label={leftContent.config?.['aria-label'] ?? 'Tock home page'}
              href={leftContent.config?.href ?? '/'}
            />
            {!isStorybook && <SiteInfo />}
          </>
        );
        break;
      case 'LeftButton':
        leftItem = (
          <IconButton
            aria-label="Back"
            component="button"
            onClick={leftContent.onClick}
            size="medium"
            variant="ghost"
          >
            <IconArrowLeft />
          </IconButton>
        );
        break;
      case 'MapButton':
        leftItem = (
          <IconButton
            aria-label="Map"
            component="button"
            onClick={leftContent.onClick}
            size="medium"
            variant="ghost"
          >
            <IconMap />
          </IconButton>
        );
        break;
      case 'TitleText':
        leftItem = <Text text={leftContent.text} />;
        break;
      case 'TitleTextWithLeftButton':
        leftItem = (
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <IconButton
              aria-label="Back"
              component="button"
              onClick={leftContent.onClick}
              size="medium"
              variant="ghost"
            >
              <IconArrowLeft />
            </IconButton>
            <div
              key="divider"
              css={({ colors, spacing }: Theme) => css`
                border-left: 1px solid ${colors.borderDefault};
                height: ${spacing(3)}px;
                margin-left: 4px;
                margin-right: 12px;
              `}
            />
            <Typography variant="heading04">{leftContent.text}</Typography>
          </div>
        );
        break;
    }

    if (leftContent.type === 'TockLogo' && centerContent.type !== 'Absent') {
      // If showing the Tock logo, it is not valid to present other center content. Ignoring.
    } else if (leftContent.type === 'TitleText' && centerContent.type === 'TitleText') {
      // If showing left-aligned title text, it is not valid to show another title in the center. Ignoring.
    } else {
      switch (centerContent.type) {
        case 'TitleText':
          centerItem = <Text text={centerContent.text} />;
          break;
        case 'TockLogo':
          centerItem = (
            <TockLogoAnchor
              aria-label={centerContent.config?.['aria-label'] ?? 'Tock home page'}
              href={centerContent.config?.href ?? '/'}
            />
          );
          break;
        case 'CenterButton':
          centerItem = centerContent.button;
          break;
      }
    }

    // For the mobile case, the auxiliary content is laid out just before the
    // hamburger button.
    switch (auxiliaryContent.type) {
      case 'AuxiliaryButtons':
        rightItems = React.Children.toArray(auxiliaryContent.buttons);
        break;
      default:
        rightItems = React.Children.toArray([]);
        break;
    }
    const rightItemInfo = transformRightContentIntoInfo(rightContent);
    if (rightItemInfo) {
      const { Icon, ariaLabel, isPrimary, onClick } = rightItemInfo;
      rightItems.push(
        <IconButton
          aria-label={ariaLabel}
          component="button"
          onClick={onClick}
          size="medium"
          variant={isPrimary ? 'primary' : 'ghost'}
          key={`right_${rightContent.type}`}
          data-testid={`${ariaLabel}-button`}
        >
          <Icon />
        </IconButton>
      );
    }
  }

  if (slimLeft === 'defaultForContent') {
    slimLeft = defaultSlimHeaderSpecForContent(leftContent, false);
  }
  if (slimRight === 'defaultForContent') {
    slimRight = defaultSlimHeaderSpecForContent(rightContent, false);
  }

  return (
    <div
      css={(theme: Theme) => [
        omakaseHeaderMobileContainer(theme),
        omakaseHeaderShadow(theme, { showShadow }),
        bumpZIndex &&
          css`
            z-index: 4;
          `,
      ]}
      {...rest}
    >
      {/* We place the center-items first in the list because their div is explicitly
          sized to that of the centaining div. If we put it later it will intercept
          all mouse events. */}
      <div
        css={[
          omakaseHeaderMobileMain,
          slimLeft === 'yes' && mobileHeaderSlimLeft,
          slimRight === 'yes' && mobileHeaderSlimRight,
        ]}
      >
        <div css={omakaseHeaderCenterItems}>{centerItem}</div>
        <div css={omakaseHeaderLeftItems}>{leftItem}</div>
        <ShrinkButtonsToIconsContext.Provider value={true}>
          <div css={omakaseHeaderRightItems}>{rightItems}</div>
        </ShrinkButtonsToIconsContext.Provider>
      </div>
      {additionalContent}
    </div>
  );
};

export default Header;
